import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import Navbar from "./components/Navbar";
import HeroSection from "./components/HeroSection";
import AboutSection from "./components/AboutSection";
import WorkSection from "./components/WorkSection";
import MainInterface from "./components/MainInterface";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import DocumentModal from "./components/DocumentModal";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { useWebSocket } from "./hooks/useWebSocket";

function App() {
  const [showInterface, setShowInterface] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const [highlightedNodes, setHighlightedNodes] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [confidence, setConfidence] = useState(100);
  
  // WebSocket hook for real-time chat
  const {
    sessionId,
    isConnected,
    isConnecting,
    error: wsError,
    sendMessage,
    clearSession,
    onResponse
  } = useWebSocket();
  
  // Track pending message ID
  const pendingMessageIdRef = useRef(null);

  // Register response callback - this gets called directly when a response arrives
  useEffect(() => {
    const unsubscribe = onResponse((data) => {
      console.log('=== CALLBACK RECEIVED ===');
      console.log('Response data:', data);
      console.log('Pending message ID:', pendingMessageIdRef.current);
      
      const { response, confidence: responseConfidence } = data;
      
      // Update the message with the response
      setMessages(prevMessages => {
        const targetId = pendingMessageIdRef.current;
        console.log('Updating messages, target ID:', targetId);
        
        const updated = prevMessages.map(m => {
          if (targetId && m.id === targetId) {
            console.log('Found message to update, changing content from "' + m.content + '" to "' + response + '"');
            return {
              ...m,
              content: response,
              confidence: responseConfidence || 0,
              isPlaceholder: false,
            };
          }
          return m;
        });
        
        console.log('Updated messages:', updated);
        return updated;
      });
      
      setConfidence(responseConfidence || 0);
      setIsLoading(false);
      pendingMessageIdRef.current = null;
    });
    
    return unsubscribe;
  }, [onResponse]);

  // Add welcome message when interface is shown
  useEffect(() => {
    if (showInterface && messages.length === 0) {
      setMessages([{
        id: "welcome",
        role: "assistant",
        content: "Hi! I'm an AI assistant powered by RAG. Ask me anything about Chethan's work, experience, projects, or skills.",
        sources: [],
        timestamp: new Date().toISOString()
      }]);
    }
  }, [showInterface, messages.length]);

  // Handle WebSocket errors
  useEffect(() => {
    // Only show a toast for real connection errors, not transient disconnects
    if (wsError && wsError.type === "error") {
      toast.error("WebSocket connection error");
      setIsLoading(false);
      pendingMessageIdRef.current = null;
    }
  }, [wsError]);

  // Handle search/chat via WebSocket
  const handleSearch = useCallback((query) => {
    if (!query.trim()) return;

    setShowInterface(true);
    setIsLoading(true);

    const userMessageId = Date.now().toString();
    const assistantMessageId = (Date.now() + 1).toString();

    // Store the pending message ID
    pendingMessageIdRef.current = assistantMessageId;
    console.log("Setting pending message ID:", assistantMessageId);

    // Add both messages at once. The assistant placeholder is marked with
    // `isPlaceholder: true` and empty content so the UI can show the ripple
    // typing indicator instead of a separate "..." bubble.
    setMessages((prev) => [
      ...prev,
      {
        id: userMessageId,
        role: "user",
        content: query,
        timestamp: new Date().toISOString(),
      },
      {
        id: assistantMessageId,
        role: "assistant",
        content: "",
        isPlaceholder: true,
        sources: [],
        timestamp: new Date().toISOString(),
      },
    ]);

    // Send message via WebSocket
    sendMessage(query);
  }, [sendMessage]);

  // Handle new chat - clear session and messages
  const handleNewChat = useCallback(() => {
    setMessages([{
      id: "welcome",
      role: "assistant",
      content: "Hi! I'm an AI assistant powered by RAG. Ask me anything about Chethan's work, experience, projects, or skills.",
      sources: [],
      timestamp: new Date().toISOString()
    }]);
    setHighlightedNodes([]);
    setConfidence(100);
    clearSession();
  }, [clearSession]);

  const handleNodeClick = useCallback((node) => {
    if (node.id === 'center' || node.category === 'category') return;
    const doc = documents.find(d => d.id === node.id);
    if (doc) setSelectedDocument(doc);
  }, [documents]);

  const scrollToInterface = () => {
    setShowInterface(true);
    setTimeout(() => {
      document.getElementById('main-interface')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Connection status indicator
  const connectionStatus = isConnecting ? "connecting" : isConnected ? "connected" : "disconnected";

  return (
    <div className="min-h-screen bg-mesh relative">
      <div className="noise-overlay fixed inset-0 pointer-events-none z-0" />
      
      <Toaster position="top-right" theme="dark" />
      
      <Navbar />
      
      <HeroSection 
        onTryIt={scrollToInterface} 
        showInterface={showInterface}
      />
      
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative z-10" id="main-interface">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-5xl font-bold gradient-gold mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Interactive Knowledge Base
            </h2>
            <p className="text-[#a0a0b0] text-lg max-w-2xl mx-auto">
              Ask questions and watch the system retrieve relevant information from my indexed documents.
            </p>
            {/* Connection status indicator */}
            <div className="mt-4 flex items-center justify-center gap-2">
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'connected' ? 'bg-green-500' :
                connectionStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' :
                'bg-red-500'
              }`} />
              <span className="text-xs text-[#606070] capitalize">{connectionStatus}</span>
              {sessionId && (
                <span className="text-xs text-[#606070] ml-2">
                  Session: {sessionId.slice(0, 8)}...
                </span>
              )}
            </div>
          </div>
          
          <MainInterface
            messages={messages}
            isLoading={isLoading}
            onSend={handleSearch}
            onNewChat={handleNewChat}
            graphData={graphData}
            highlightedNodes={highlightedNodes}
            onNodeClick={handleNodeClick}
            onSourceClick={(source) => {
              const doc = documents.find(d => d.id === source.id);
              if (doc) setSelectedDocument(doc);
            }}
            confidence={confidence}
          />
        </div>
      </section>
      
      <AboutSection />
      
      <WorkSection />
      
      <ContactSection />
      
      <Footer />
      
      <DocumentModal 
        document={selectedDocument}
        onClose={() => setSelectedDocument(null)}
      />
    </div>
  );
}

export default App;